<template>
    <div class="ds-ex-grid ds-ex-row ds-ex-grid-requests" :class="{ 'is-decision': incoming && pending }">
        <div class="ds-ex-when">
            <div class="ds-ex-brand" :class="{ 'ds-ex-strong': incoming && pending }">{{ dateLabel }}</div>
            <div class="ds-ex-muted">{{ time }}</div>
        </div>
        <div class="ds-ex-min0">
            <template v-if="incoming">
                <div class="ds-ex-brand ds-ex-ellipsis">{{ request.department.name }}</div>
                <div v-if="requesterText" class="ds-ex-muted ds-ex-ellipsis">{{ requesterText }}</div>
            </template>
            <div v-else class="ds-ex-brand ds-ex-ellipsis">{{ otherStoreName }}</div>
        </div>
        <div class="ds-ex-responses">
            <template v-if="person">
                <ExchangeAvatar :person="person" />
                <div class="ds-ex-min0">
                    <div class="ds-ex-ellipsis" :class="{ 'ds-ex-strong': incoming && pending }">{{ fullName(person) }}</div>
                    <div v-if="person.position?.name" class="ds-ex-muted ds-ex-ellipsis">{{ person.position.name }}</div>
                </div>
            </template>
            <span v-else class="ds-ex-muted">—</span>
        </div>
        <div class="ds-ex-brand ds-ex-ellipsis">{{ request.position.name }}</div>
        <div><span class="ds-ex-chip" :class="`is-${chip.tone}`">{{ chip.label }}</span></div>
        <div class="ds-ex-actions">
            <template v-if="incoming">
                <template v-if="pending && !started">
                    <button type="button" class="ds-glass-btn ds-glass-btn-secondary" :disabled="busy" @click="emit('reject')">Отклонить</button>
                    <button v-if="canApprove" type="button" class="ds-glass-btn ds-glass-btn-primary" :disabled="busy" @click="emit('approve')">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
                        Согласовать
                    </button>
                </template>
                <span v-else class="ds-ex-muted">{{ answerText }}</span>
            </template>
            <template v-else>
                <button v-if="rejected" type="button" class="ds-glass-btn ds-glass-btn-secondary" @click="emit('ask-other')">Запросить другого</button>
                <span v-else class="ds-ex-muted">{{ outgoingText }}</span>
                <ExchangeRowMenu :items="menuItems" @select="onMenu" />
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import dayjs from 'dayjs'
import ExchangeAvatar from './ExchangeAvatar.vue'
import ExchangeRowMenu, { type RowMenuItem } from './ExchangeRowMenu.vue'
import { dayKeyOf, fullName, requestChip, rowDateLabel, shortName, timeRange, toLocal, todayKey, workMs } from './exchangeFormat'
import type { Vacancy } from './exchangeTypes'

const props = defineProps<{
    request: Vacancy
    // Направление — по ID подразделения профиля, не по названию.
    incoming: boolean
    otherStoreName: string
    busy: boolean
    canApprove: boolean
    canManage: boolean
    now: number
}>()

const emit = defineEmits<{ approve: [], reject: [], 'ask-other': [], history: [], remove: [] }>()

const dateLabel = computed(() => rowDateLabel(dayKeyOf(props.request.start)))
const time = computed(() => timeRange(props.request))
const chip = computed(() => requestChip(props.request))
const rejected = computed(() => props.request.lifecycle === 'rejected' || props.request.isRequestRejected)
const closed = computed(() => props.request.lifecycle === 'closed' || (!props.request.lifecycle && props.request.status === 'completed'))
const pending = computed(() => !rejected.value && !closed.value)
const started = computed(() => workMs(props.request.start) <= props.now)
const person = computed(() => props.request.assigned ?? props.request.requestedUser)

const requesterText = computed(() => {
    const parts = [shortName(props.request.createdBy)]
    if (props.request.createdAt) {
        const created = toLocal(props.request.createdAt)
        const day = created.format('YYYY-MM-DD')
        const today = todayKey()
        const dayText = day === today ? 'сегодня' : day === dayjs(today).subtract(1, 'day').format('YYYY-MM-DD') ? 'вчера' : created.format('DD.MM')
        parts.push(`${dayText} ${created.format('HH:mm')}`)
    }
    return parts.filter(Boolean).join(' · ')
})

// Дата ответа в DTO отсутствует — подпись без даты.
const answerText = computed(() => {
    if (closed.value) return 'Согласовано'
    if (rejected.value) return 'Отклонено'
    return 'Начало наступило'
})

const outgoingText = computed(() => {
    if (closed.value) return props.request.assignedShiftId ? 'Смена в расписании' : 'Согласовано'
    return 'ждёт ответа директора'
})

const menuItems = computed<RowMenuItem[]>(() => {
    const items: RowMenuItem[] = [{ id: 'history', label: 'История' }]
    if (props.canManage && props.request.canDelete) items.push({ id: 'remove', label: 'Удалить запрос', danger: true, disabled: props.busy })
    return items
})

function onMenu(id: string) {
    if (id === 'history') emit('history')
    else if (id === 'remove') emit('remove')
}
</script>
